export type JobProcessingUnit = 'min' | 'hr' | 'day'

export type JobScheduleDateTimeParts = {
  date: string
  time: string
}

function padDatePart(value: number) {
  return String(value).padStart(2, '0')
}

export function formatLocalDateTimeParts(date: Date): JobScheduleDateTimeParts {
  return {
    date: `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`,
    time: `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`,
  }
}

export function splitJobDateTime(value?: string | null): JobScheduleDateTimeParts {
  const trimmed = value?.trim()
  if (!trimmed) {
    return { date: '', time: '' }
  }

  const parsed = new Date(trimmed)
  if (Number.isFinite(parsed.getTime())) {
    return formatLocalDateTimeParts(parsed)
  }

  if (trimmed.includes('T')) {
    const [datePart, timePart = ''] = trimmed.split('T')
    return {
      date: datePart,
      time: timePart.slice(0, 5),
    }
  }

  return {
    date: trimmed.slice(0, 10),
    time: '',
  }
}

export function combineJobDateTime(date?: string, time?: string): string | undefined {
  const normalizedDate = date?.trim()
  if (!normalizedDate) {
    return undefined
  }

  const normalizedTime = time?.trim() || '00:00'
  const withSeconds = normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime
  return `${normalizedDate}T${withSeconds}`
}

export function processingDurationMs(
  processingTime?: string | number | null,
  processingUnit?: JobProcessingUnit | string | null,
): number | null {
  if (processingTime === null || processingTime === undefined || processingTime === '') {
    return null
  }

  const amount = typeof processingTime === 'number' ? processingTime : Number(processingTime)
  if (!Number.isFinite(amount) || amount <= 0) {
    return null
  }

  const unit = String(processingUnit ?? 'hr').toLowerCase()
  if (unit === 'min') {
    return amount * 60_000
  }
  if (unit === 'day') {
    return amount * 24 * 60 * 60_000
  }
  return amount * 60 * 60_000
}

export function computeJobEndFromProcessing(
  startDate?: string,
  startTime?: string,
  processingTime?: string | number | null,
  processingUnit?: JobProcessingUnit | string | null,
): JobScheduleDateTimeParts | null {
  const startIso = combineJobDateTime(startDate, startTime)
  const durationMs = processingDurationMs(processingTime, processingUnit)
  if (!startIso || durationMs == null) {
    return null
  }

  const startMs = new Date(startIso).getTime()
  if (!Number.isFinite(startMs)) {
    return null
  }

  return formatLocalDateTimeParts(new Date(startMs + durationMs))
}

export function deriveProcessingFromSchedule(
  start?: string | null,
  end?: string | null,
  scheduledHours?: number | null,
): { processing_time: string; processing_unit: JobProcessingUnit } {
  const startIso = start?.trim()
  const endIso = end?.trim()

  if (startIso && endIso) {
    const startMs = new Date(startIso).getTime()
    const endMs = new Date(endIso).getTime()
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      const totalMinutes = Math.round((endMs - startMs) / 60_000)
      if (totalMinutes > 0 && totalMinutes % (24 * 60) === 0) {
        return { processing_time: String(totalMinutes / (24 * 60)), processing_unit: 'day' }
      }
      if (totalMinutes > 0 && totalMinutes % 60 === 0) {
        return { processing_time: String(totalMinutes / 60), processing_unit: 'hr' }
      }
      if (totalMinutes > 0) {
        return { processing_time: String(totalMinutes), processing_unit: 'min' }
      }
    }
  }

  if (scheduledHours != null && Number.isFinite(Number(scheduledHours)) && Number(scheduledHours) > 0) {
    return { processing_time: String(scheduledHours), processing_unit: 'hr' }
  }

  return { processing_time: '', processing_unit: 'hr' }
}

export function scheduledHoursFromSchedule(
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
  processingTime?: string | number | null,
  processingUnit?: JobProcessingUnit | string | null,
): number | undefined {
  const startIso = combineJobDateTime(startDate, startTime)
  const endIso = combineJobDateTime(endDate, endTime)

  if (startIso && endIso) {
    const startMs = new Date(startIso).getTime()
    const endMs = new Date(endIso).getTime()
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      return Math.round(((endMs - startMs) / 3_600_000) * 100) / 100
    }
  }

  const durationMs = processingDurationMs(processingTime, processingUnit)
  if (durationMs != null) {
    return Math.round((durationMs / 3_600_000) * 100) / 100
  }

  return undefined
}

const SCHEDULE_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
}

export function formatJobScheduleDateTime(
  value?: string | null,
  locale?: string,
  timeZone?: string,
) {
  const trimmed = value?.trim()
  if (!trimmed) {
    return null
  }

  const parsed = new Date(trimmed)
  if (!Number.isFinite(parsed.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat(locale, {
    ...SCHEDULE_DATE_TIME_OPTIONS,
    ...(timeZone ? { timeZone } : {}),
  }).format(parsed)
}

export function resolveJobScheduleDurationMinutes(
  start?: string | null,
  end?: string | null,
  scheduledHours?: number | null,
): number | null {
  const startIso = start?.trim()
  const endIso = end?.trim()

  if (startIso && endIso) {
    const startMs = new Date(startIso).getTime()
    const endMs = new Date(endIso).getTime()
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      return Math.round((endMs - startMs) / 60_000)
    }
  }

  if (scheduledHours != null && Number.isFinite(Number(scheduledHours)) && Number(scheduledHours) > 0) {
    return Math.round(Number(scheduledHours) * 60)
  }

  return null
}

export function formatJobScheduleDuration(
  start?: string | null,
  end?: string | null,
  scheduledHours?: number | null,
) {
  const totalMinutes = resolveJobScheduleDurationMinutes(start, end, scheduledHours)
  if (totalMinutes == null || totalMinutes <= 0) {
    return null
  }

  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60
  const parts: string[] = []

  if (days > 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'}`)
  }
  if (hours > 0) {
    parts.push(`${hours} hr`)
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`)
  }

  return parts.join(' ')
}
